import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Paper, Button, CircularProgress } from '@material-ui/core';
import CheckboxTree from 'react-checkbox-tree';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import FolderIcon from '@material-ui/icons/Folder';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import DescriptionTwoToneIcon from '@material-ui/icons/DescriptionTwoTone';

import * as strings from '../../helpers/strings';

class DownloadReportPortfolioTable extends Component {
  state = { checked: [], expanded: [] };

  componentDidUpdate(prevProps) {
    if (prevProps.files !== this.props.files) {
      const checked = this.props.files.map(this.getFilePath).filter(Boolean);
      const months = Object.keys(this.groupFilesByMonth(this.props.files, this.props.month));
      const expanded = this.props.files.length > 0 ? [
        `year:${this.props.year}`,
        ...(this.props.isPortfolio ? months.map(month => `month:${month}`) : []),
      ] : [];
      this.setState({ checked, expanded });
    }
  }

  getFilePath = file => typeof file === 'string'
    ? file
    : (file.path || file.key || file.fileName || '');

  getFileName = file => {
    const parts = this.getFilePath(file).split('/');
    return parts[parts.length - 1];
  };

  getFileSize = file => typeof file === 'string' ? '' : (file.fileSize || '');

  getFileMonth = (file, fallbackMonth) => {
    const pathParts = this.getFilePath(file).split('/');
    const monthFromPath = pathParts.find(part => /^\d{4}-\d{2}$/.test(part));
    if (monthFromPath) return monthFromPath;

    const monthFromName = this.getFileName(file).match(/\d{4}-\d{2}/);
    return monthFromName ? monthFromName[0] : (fallbackMonth || '');
  };

  groupFilesByMonth = (files, fallbackMonth) => files.reduce((groups, file) => {
    const fileMonth = this.getFileMonth(file, fallbackMonth);
    if (!groups[fileMonth]) groups[fileMonth] = [];
    groups[fileMonth].push(file);
    return groups;
  }, {});

  renderFileLabel = (file, isPortfolio) => (
    <span className={`portfolioReportFileRow${isPortfolio ? '' : ' portfolioReportFileRowSingle'}`}>
      <span className="portfolioReportFileName">{this.getFileName(file)}</span>
      <span className="portfolioReportFileSize">{this.getFileSize(file)}</span>
    </span>
  );

  render() {
    const { files, year, month, isPortfolio, isLoading, isDownloading, onDownload, t } = this.props;
    const fileByPath = files.reduce((result, file) => {
      result[this.getFilePath(file)] = file;
      return result;
    }, {});
    const filesByMonth = this.groupFilesByMonth(files, month);
    const fileNodes = files.map(file => ({
      value: this.getFilePath(file),
      label: this.renderFileLabel(file, isPortfolio),
      icon: <DescriptionTwoToneIcon color="primary" />,
    }));
    const nodes = files.length > 0 ? [{
      value: `year:${year}`,
      label: `${t('year')}: ${year}`,
      showCheckbox: false,
      children: isPortfolio ? Object.keys(filesByMonth).sort().map(fileMonth => ({
        value: `month:${fileMonth}`,
        label: `${t('month')}: ${fileMonth} (${t('total-reports-count', { count: filesByMonth[fileMonth].length })})`,
        children: filesByMonth[fileMonth].map(file => ({
          value: this.getFilePath(file),
          label: this.renderFileLabel(file, isPortfolio),
          icon: <DescriptionTwoToneIcon color="primary" />,
        })),
      })) : fileNodes,
    }] : [];
    const selectedFiles = this.state.checked.map(path => fileByPath[path]).filter(Boolean);

    return (
      <Paper className="downloadReportPortfolioResult">
        <div className="portfolioReportHeader">
          <span>{t('download-report-list')}</span>
          <span>{t('file-size')}</span>
        </div>
        <div className="portfolioReportTree">
          {isLoading ? (
            <div className="portfolioReportTableLoader" role="status" aria-label={t('loading')}>
              <CircularProgress size={36} thickness={4} />
              <span>{t('loading')}</span>
            </div>
          ) : files.length > 0 ? (
            <CheckboxTree
              nodes={nodes}
              checked={this.state.checked}
              expanded={this.state.expanded}
              onCheck={checked => this.setState({ checked })}
              onExpand={expanded => this.setState({ expanded })}
              icons={{
                check: <CheckBoxIcon color="primary" />,
                uncheck: <CheckBoxOutlineBlankIcon color="primary" />,
                halfCheck: <IndeterminateCheckBoxIcon color="primary" />,
                expandClose: <AddIcon color="primary" />,
                expandOpen: <RemoveIcon color="primary" />,
                parentClose: <FolderIcon color="primary" />,
                parentOpen: <FolderIcon color="primary" />,
              }}
            />
          ) : (
            <div className="portfolioReportEmpty">{t('no-download-links-report')}</div>
          )}
        </div>
        <div className="downloadReportPortfolioDownload">
          <Button
            variant={strings.btnRaised}
            color={strings.colorPrimary}
            disabled={isLoading || selectedFiles.length === 0 || isDownloading}
            onClick={() => onDownload(selectedFiles)}
          >
            {t('download')}
          </Button>
        </div>
      </Paper>
    );
  }
}

export default withTranslation()(DownloadReportPortfolioTable);
